import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { Box } from "@mui/system";
import {
  Button,
  Typography,
  Card,
  TextField,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";

import {
  ADD_SUB_PACKAGE_VARIANT,
  ALL_SUB_PACKAGE_VARIANT,
} from "../../gql/subscriptionpackageVariant";

const sectionCard = {
  borderRadius: "12px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  p: 3,
  mb: 2.5,
};

const CreateSubPackageVariant = ({ handleClose, packageId }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    delete errors[prop];
    setErrors({ ...errors });
  };

  const [createVariant] = useMutation(ADD_SUB_PACKAGE_VARIANT, {
    onError: (error) => {
      console.log("error : ", error);
      alert("Sub Package Variant Upload Error");
      setLoading(false);
    },
    onCompleted: (result) => {
      setValues({});
      setErrors({});
      setLoading(false);
      alert("New Sub Package Variant has been Created");
      handleClose();
    },
    refetchQueries: [ALL_SUB_PACKAGE_VARIANT],
  });

  const handleCreate = async () => {
    setLoading(true);
    setErrors({});
    let isErrorExit = false;
    let errorObject = {};
    if (!values.title_en) {
      errorObject.title_en = "English title is required.";
      isErrorExit = true;
    }
    if (!values.price) {
      errorObject.price = "Price is required.";
      isErrorExit = true;
    }
    if (!values.title_mm) {
      errorObject.title_mm = "Myanmar title is required.";
      isErrorExit = true;
    }
    if (!values.durationInDays) {
      errorObject.durationInDays = "Duration (in days) is required.";
      isErrorExit = true;
    }
    if (!values.description_en) {
      errorObject.description_en = "English description is required.";
      isErrorExit = true;
    }
    if (!values.description_mm) {
      errorObject.description_mm = "Myanmar description is required.";
      isErrorExit = true;
    }
    if (!values.original_price) {
      errorObject.original_price = "Original price is required.";
      isErrorExit = true;
    }
    if (!values.discount_percentage) {
      errorObject.discount_percentage = "Discount percentage is required.";
      isErrorExit = true;
    }

    if (isErrorExit) {
      setErrors({ ...errorObject });
      setLoading(false);
      return;
    }

    try {
      await createVariant({
        variables: {
          addSubPackageVariant: {
            title_en: values.title_en,
            title_mm: values.title_mm,
            price: parseInt(values.price),
            original_price: values?.original_price
              ? parseInt(values.original_price)
              : 0,
            description_en: values.description_en,
            description_mm: values.description_mm,
            discount_percentage: values?.discount_percentage
              ? parseInt(values.discount_percentage)
              : 0,
            durationInDays: parseInt(values.durationInDays),
            subscription_package_id: packageId,
          },
        },
      });
    } catch (error) {
      console.log("error : ", error);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eee",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Create Subscription Package Variant
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Configure a pricing and duration variant for this subscription package
          </Typography>
        </Box>
        <Button onClick={handleClose} variant="outlined" color="inherit" size="small" sx={{ borderColor: "#ddd", color: "#555" }}>
          Close
        </Button>
      </Box>

      <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 4 }}>
        {/* Title Details Card */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Titles</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              id="title_en"
              label="Title (English) *"
              fullWidth
              value={values.title_en || ""}
              onChange={handleChange("title_en")}
              error={!!errors.title_en}
              helperText={errors.title_en}
            />
            <TextField
              id="title_mm"
              label="Title (Myanmar) *"
              fullWidth
              value={values.title_mm || ""}
              onChange={handleChange("title_mm")}
              error={!!errors.title_mm}
              helperText={errors.title_mm}
            />
          </Box>
        </Card>

        {/* Pricing Details Card */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Pricing & Duration</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              type="number"
              id="price"
              label="Actual Sale Price *"
              fullWidth
              value={values.price || ""}
              onChange={handleChange("price")}
              error={!!errors.price}
              helperText={errors.price}
            />
            <TextField
              type="number"
              id="original_price"
              label="Original Price (Before Discount) *"
              fullWidth
              value={values.original_price || ""}
              onChange={handleChange("original_price")}
              error={!!errors.original_price}
              helperText={errors.original_price}
            />
            <TextField
              type="number"
              id="discount_percentage"
              label="Discount Percentage *"
              fullWidth
              value={values.discount_percentage || ""}
              onChange={handleChange("discount_percentage")}
              error={!!errors.discount_percentage}
              helperText={errors.discount_percentage}
            />
            <TextField
              type="number"
              id="durationInDays"
              label="Duration (in days) *"
              fullWidth
              value={values.durationInDays || ""}
              onChange={handleChange("durationInDays")}
              error={!!errors.durationInDays}
              helperText={errors.durationInDays}
            />
          </Box>
        </Card>

        {/* Description Details Card */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Descriptions</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              id="description_en"
              label="Description (English) *"
              fullWidth
              multiline
              minRows={3}
              value={values.description_en || ""}
              onChange={handleChange("description_en")}
              error={!!errors.description_en}
              helperText={errors.description_en}
            />
            <TextField
              id="description_mm"
              label="Description (Myanmar) *"
              fullWidth
              multiline
              minRows={3}
              value={values.description_mm || ""}
              onChange={handleChange("description_mm")}
              error={!!errors.description_mm}
              helperText={errors.description_mm}
            />
          </Box>
        </Card>

        {/* Footer Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1, pb: 4 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit" size="large" sx={{ borderColor: "#ddd", color: "#555", minWidth: 100 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={handleCreate}
            sx={{
              minWidth: 140,
              bgcolor: "#ff5722",
              "&:hover": { bgcolor: "#e64a19" },
              "&.Mui-disabled": { bgcolor: "#ffccbc", color: "#fff" },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
            {loading ? "Creating..." : "Create Variant"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateSubPackageVariant;
